import { useI18n } from '@/lib/i18n'
import { trpcPostClient, useTRPC } from '@/lib/trpc'
import { useMutation, useQueries } from '@tanstack/react-query'
import { cleanTtsText } from '@repo/sdk'
import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { TextLogSpeechReference } from './speech-references'

const CACHE_STATUS_BATCH_SIZE = 100
export const TEXT_LOG_TTS_PLAYBACK_RATE = 1.2
const SILENT_UNLOCK_AUDIO
  = 'data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEAwF0AAIC7AAACABAAZGF0YQIAAAAAAA=='

let activePlayback: { owner: symbol, stop: () => void } | null = null

function claimPlayback(owner: symbol, stop: () => void) {
  if (activePlayback?.owner !== owner)
    activePlayback?.stop()
  activePlayback = { owner, stop }
}

function releasePlayback(owner: symbol) {
  if (activePlayback?.owner === owner)
    activePlayback = null
}

export function stopActiveSpeechPlayback() {
  activePlayback?.stop()
}

function audioIdentity(reference: TextLogSpeechReference): string {
  return JSON.stringify([
    cleanTtsText(reference.speech),
    reference.voiceName,
    reference.language,
  ])
}

function chunkReferences(
  references: readonly TextLogSpeechReference[],
): TextLogSpeechReference[][] {
  const chunks: TextLogSpeechReference[][] = []
  for (let index = 0; index < references.length; index += CACHE_STATUS_BATCH_SIZE)
    chunks.push(references.slice(index, index + CACHE_STATUS_BATCH_SIZE))
  return chunks
}

function writeAscii(view: DataView, offset: number, value: string): void {
  for (let index = 0; index < value.length; index++)
    view.setUint8(offset + index, value.codePointAt(index)!)
}

function wrapPcm16MonoAsWav(bytes: Uint8Array, mime: string): Blob {
  const rateMatch = mime.match(/(?:^|;)\s*rate\s*=\s*(\d+)/i)
  const parsedRate = rateMatch ? Number(rateMatch[1]) : 24_000
  const sampleRate = Number.isSafeInteger(parsedRate)
    && parsedRate >= 8_000
    && parsedRate <= 192_000
    ? parsedRate
    : 24_000
  const headerBytes = 44
  const sampleBytes = 2
  const buffer = new ArrayBuffer(headerBytes + bytes.byteLength)
  const view = new DataView(buffer)

  writeAscii(view, 0, 'RIFF')
  view.setUint32(4, 36 + bytes.byteLength, true)
  writeAscii(view, 8, 'WAVE')
  writeAscii(view, 12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * sampleBytes, true)
  view.setUint16(32, sampleBytes, true)
  view.setUint16(34, 16, true)
  writeAscii(view, 36, 'data')
  view.setUint32(40, bytes.byteLength, true)
  new Uint8Array(buffer, headerBytes).set(bytes)

  return new Blob([buffer], { type: 'audio/wav' })
}

function audioBlobFromBase64(base64: string, mime: string): Blob {
  const binary = window.atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index++)
    bytes[index] = binary.codePointAt(index)!
  if (mime.toLowerCase().startsWith('audio/pcm'))
    return wrapPcm16MonoAsWav(bytes, mime)
  return new Blob([bytes], { type: mime })
}

export interface SpeechPlaybackController {
  pendingId: string | null
  playingId: string | null
  isCached: (reference: TextLogSpeechReference) => boolean
  isChecking: (reference: TextLogSpeechReference) => boolean
  isStatusError: (reference: TextLogSpeechReference) => boolean
  play: (reference: TextLogSpeechReference) => Promise<void>
  stop: () => void
}

export function useSpeechPlayback(
  references: readonly TextLogSpeechReference[],
  scopeKey: string,
  settings: { playbackRate?: number, muted?: boolean } = {},
): SpeechPlaybackController {
  const trpc = useTRPC()
  const { t } = useI18n()
  const eligibleReferences = useMemo(
    () => references.filter(reference => reference.ttsEligible),
    [references],
  )
  const chunks = useMemo(
    () => chunkReferences(eligibleReferences),
    [eligibleReferences],
  )
  const statusQueries = useQueries({
    queries: chunks.map(chunk => ({
      queryKey: [
        'text-log-tts-cache-status',
        chunk.map(reference => [
          reference.id,
          reference.speech,
          reference.voiceName,
          reference.language,
        ]),
      ],
      queryFn: () => trpcPostClient.playRouter.getTTSCacheStatus.query({
        items: chunk.map(reference => ({
          id: reference.id,
          text: reference.speech,
          voiceName: reference.voiceName,
          language: reference.language,
        })),
      }),
      staleTime: Infinity,
      retry: 1,
    })),
  })
  const serverCached = useMemo(() => {
    const result = new Map<string, boolean>()
    for (const query of statusQueries) {
      for (const item of query.data ?? [])
        result.set(item.id, item.cached)
    }
    return result
  }, [statusQueries])
  const checkingIds = useMemo(() => {
    const result = new Set<string>()
    chunks.forEach((chunk, index) => {
      if (statusQueries[index]?.isPending) {
        for (const reference of chunk)
          result.add(reference.id)
      }
    })
    return result
  }, [chunks, statusQueries])
  const statusErrorIds = useMemo(() => {
    const result = new Set<string>()
    chunks.forEach((chunk, index) => {
      if (statusQueries[index]?.isError) {
        for (const reference of chunk)
          result.add(reference.id)
      }
    })
    return result
  }, [chunks, statusQueries])

  const generateTTS = useMutation(
    trpc.playRouter.generateTTS.mutationOptions(),
  )
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const [locallyCached, setLocallyCached] = useState<Set<string>>(
    () => new Set(),
  )
  const audioBlobsRef = useRef(new Map<string, Blob>())
  const audioElementRef = useRef<HTMLAudioElement | null>(null)
  const currentAudioUrlRef = useRef<string | null>(null)
  const endedHandlerRef = useRef<(() => void) | null>(null)
  const errorHandlerRef = useRef<(() => void) | null>(null)
  const pendingRef = useRef(false)
  const requestTokenRef = useRef(0)
  const ownerRef = useRef(Symbol('speech-playback'))
  const settingsRef = useRef(settings)
  settingsRef.current = settings
  const scopeKeyRef = useRef(scopeKey)
  scopeKeyRef.current = scopeKey

  const clearAudioHandlers = useCallback((audio: HTMLAudioElement) => {
    if (endedHandlerRef.current) {
      audio.removeEventListener('ended', endedHandlerRef.current)
      endedHandlerRef.current = null
    }
    if (errorHandlerRef.current) {
      audio.removeEventListener('error', errorHandlerRef.current)
      errorHandlerRef.current = null
    }
  }, [])

  const stopCurrentAudio = useCallback(() => {
    const audio = audioElementRef.current
    if (audio) {
      clearAudioHandlers(audio)
      audio.pause()
      audio.currentTime = 0
      audio.removeAttribute('src')
      audio.load()
    }
    if (currentAudioUrlRef.current) {
      URL.revokeObjectURL(currentAudioUrlRef.current)
      currentAudioUrlRef.current = null
    }
    setPlayingId(null)
  }, [clearAudioHandlers])

  const stop = useCallback(() => {
    requestTokenRef.current++
    pendingRef.current = false
    setPendingId(null)
    stopCurrentAudio()
    releasePlayback(ownerRef.current)
  }, [stopCurrentAudio])

  useEffect(() => {
    const audio = audioElementRef.current
    if (!audio)
      return
    audio.muted = settings.muted ?? false
    audio.playbackRate = settingsRef.current.playbackRate ?? TEXT_LOG_TTS_PLAYBACK_RATE
  }, [settings.muted, settings.playbackRate])

  useEffect(() => {
    stop()
  }, [scopeKey, stop])

  useEffect(() => () => {
    requestTokenRef.current++
    pendingRef.current = false
    stopCurrentAudio()
    releasePlayback(ownerRef.current)
    audioBlobsRef.current.clear()
    audioElementRef.current = null
  }, [stopCurrentAudio])

  const play = useCallback(async (reference: TextLogSpeechReference) => {
    if (!reference.ttsEligible || pendingRef.current)
      return

    claimPlayback(ownerRef.current, stop)
    pendingRef.current = true
    const requestToken = ++requestTokenRef.current
    const requestScope = scopeKeyRef.current
    const identity = audioIdentity(reference)
    setPendingId(reference.id)
    stopCurrentAudio()
    const audio = audioElementRef.current ?? new Audio()
    audioElementRef.current = audio
    audio.defaultPlaybackRate = TEXT_LOG_TTS_PLAYBACK_RATE
    audio.playbackRate = settingsRef.current.playbackRate ?? TEXT_LOG_TTS_PLAYBACK_RATE
    audio.src = SILENT_UNLOCK_AUDIO
    audio.volume = 0
    void audio.play().catch(() => {})

    try {
      let audioBlob = audioBlobsRef.current.get(identity)
      if (!audioBlob) {
        const result = await generateTTS.mutateAsync({
          text: reference.speech,
          voiceName: reference.voiceName,
          language: reference.language,
        })
        if (
          requestToken !== requestTokenRef.current
          || requestScope !== scopeKeyRef.current
        ) {
          return
        }
        audioBlob = audioBlobFromBase64(result.base64, result.mime)
        audioBlobsRef.current.set(identity, audioBlob)
        setLocallyCached((current) => {
          const next = new Set(current)
          next.add(identity)
          return next
        })
      }

      if (
        requestToken !== requestTokenRef.current
        || requestScope !== scopeKeyRef.current
      ) {
        return
      }

      const audioUrl = URL.createObjectURL(audioBlob)
      currentAudioUrlRef.current = audioUrl
      const playbackAudio = audioElementRef.current
      if (playbackAudio !== audio) {
        URL.revokeObjectURL(audioUrl)
        currentAudioUrlRef.current = null
        return
      }
      const releaseAudio = () => {
        if (currentAudioUrlRef.current !== audioUrl)
          return
        clearAudioHandlers(playbackAudio)
        URL.revokeObjectURL(audioUrl)
        currentAudioUrlRef.current = null
      }
      const handleEnded = () => {
        releaseAudio()
        stopCurrentAudio()
        releasePlayback(ownerRef.current)
      }
      const handleError = () => {
        releaseAudio()
        stopCurrentAudio()
        releasePlayback(ownerRef.current)
        toast.error(t('play.tts.playbackFailed'))
      }
      endedHandlerRef.current = handleEnded
      errorHandlerRef.current = handleError
      playbackAudio.addEventListener('ended', handleEnded)
      playbackAudio.addEventListener('error', handleError)
      playbackAudio.src = audioUrl
      playbackAudio.volume = 1
      playbackAudio.currentTime = 0
      playbackAudio.defaultPlaybackRate = TEXT_LOG_TTS_PLAYBACK_RATE
      playbackAudio.playbackRate = settingsRef.current.playbackRate ?? TEXT_LOG_TTS_PLAYBACK_RATE
      playbackAudio.muted = settingsRef.current.muted ?? false
      try {
        await playbackAudio.play()
        if (
          requestToken === requestTokenRef.current
          && requestScope === scopeKeyRef.current
          && currentAudioUrlRef.current === audioUrl
        ) {
          setPlayingId(reference.id)
        }
      }
      catch {
        releaseAudio()
        stopCurrentAudio()
        releasePlayback(ownerRef.current)
        toast.error(t('play.tts.playbackFailed'))
      }
    }
    catch {
      if (
        requestToken === requestTokenRef.current
        && requestScope === scopeKeyRef.current
      ) {
        stopCurrentAudio()
        releasePlayback(ownerRef.current)
        toast.error(t('play.tts.failed'))
      }
    }
    finally {
      if (
        requestToken === requestTokenRef.current
        && requestScope === scopeKeyRef.current
      ) {
        pendingRef.current = false
        setPendingId(null)
      }
    }
  }, [clearAudioHandlers, generateTTS, stop, stopCurrentAudio, t])

  return {
    pendingId,
    playingId,
    isCached: reference =>
      locallyCached.has(audioIdentity(reference))
      || serverCached.get(reference.id) === true,
    isChecking: reference => checkingIds.has(reference.id),
    isStatusError: reference => statusErrorIds.has(reference.id),
    play,
    stop,
  }
}

export function SpeechPlaybackStatusAccent({
  reference,
  controller,
}: {
  reference: TextLogSpeechReference
  controller: SpeechPlaybackController
}) {
  const cached = controller.isCached(reference)

  return (
    <span
      aria-hidden="true"
      className={`not-prose inline-block text-sm ${cached ? 'grayscale-0' : 'opacity-45 grayscale'}`}
      data-testid="speech-tts-heading-accent"
      data-speech-id={reference.id}
      data-tts-state={cached ? 'cached' : 'uncached'}
    >
      📢
    </span>
  )
}

export function SpeechPlaybackControl({
  reference,
  controller,
}: {
  reference: TextLogSpeechReference
  controller: SpeechPlaybackController
}) {
  const { t } = useI18n()
  const pending = controller.pendingId === reference.id
  const cached = controller.isCached(reference)
  const checking = controller.isChecking(reference)
  const statusError = controller.isStatusError(reference)
  const disabled = controller.pendingId !== null
  const state = pending
    ? 'pending'
    : cached
      ? 'cached'
      : checking
        ? 'checking'
        : statusError
          ? 'error'
          : 'uncached'
  const label = pending
    ? t('play.tts.generating')
    : cached
      ? `${t('play.tts.play')}. ${t('play.tts.cached')}`
      : checking
        ? `${t('play.tts.generate')}. ${t('play.tts.checking')}`
        : statusError
          ? `${t('play.tts.generate')}. ${t('play.tts.statusFailed')}`
          : t('play.tts.generate')

  return (
    <button
      type="button"
      className="not-prose ml-1 inline-flex size-6 items-center justify-center rounded-sm align-middle text-sm transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60"
      aria-label={label}
      aria-busy={pending}
      title={label}
      disabled={disabled}
      data-testid="speech-tts-control"
      data-speech-id={reference.id}
      data-speech-key={reference.key}
      data-tts-state={state}
      onClick={() => { controller.play(reference).catch(() => {}) }}
    >
      {pending ? (
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
      ) : (
        <span
          aria-hidden="true"
          className={cached ? 'grayscale-0' : 'opacity-45 grayscale'}
        >
          📢
        </span>
      )}
      <span className="sr-only" aria-live="polite">{label}</span>
    </button>
  )
}
